import { useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, ImagePlus, Plus, Send, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Spinner } from "@/components/Bits";
import { useApi, Badge, EntityForm, STATUS_FR } from "@/pages/admin/regie/ui";
import { usePartners } from "@/pages/admin/regie/Catalog";
import MockPage, { SAMPLE_AD } from "@/pages/admin/regie/MockPage";
import CreativeFit from "@/pages/admin/regie/CreativeFit";
import CreativeLayout from "@/pages/admin/regie/CreativeLayout";

const ORIGIN = process.env.REACT_APP_BACKEND_URL;
const TEXT_FIELDS = [
  { k: "title", label: "Titre (70 car.)", req: true }, { k: "subtitle", label: "Sous-titre" }, { k: "body", label: "Texte (240 car.)", type: "textarea", max: 240 },
  { k: "cta", label: "Texte du bouton", default: "Découvrir" }, { k: "url", label: "Lien de destination (https://…)", req: true }, { k: "code", label: "Code promo (facultatif)" },
  { k: "label", label: "Mention", type: "select", default: "Publicité", options: [["Publicité", "Publicité"], ["Partenaire", "Partenaire"], ["Offre partenaire", "Offre partenaire"]] },
  { k: "alt", label: "Texte alternatif de l'image" },
];
const KEEP = ["partner_id", "format", "country"];

export const adFrom = (cr, partner) => (cr ? {
  ...cr, preview: true, partner: partner.name, partner_color: partner.brand_color, partner_logo_url: partner.logo_url,
  image_url: cr.image_version ? `/api/ads/creative-image/${cr.id}?v=${cr.image_version}` : null,
} : { ...SAMPLE_AD, partner: partner.name, partner_color: partner.brand_color, partner_logo_url: partner.logo_url });

function PlacementTile({ pl, ad, selected, onPick }) {
  const pending = pl.pending_campaign_ids.length > 0;
  return (
    <button type="button" onClick={onPick} aria-pressed={selected} data-testid={`ws-placement-${pl.code}`} data-live={pl.live ? "1" : "0"}
      className={`mpl-card flex min-w-0 flex-col gap-2 p-3 text-left transition-[opacity,transform,box-shadow] hover:-translate-y-0.5 ${selected ? "ring-2 ring-[#B7F52A]" : ""} ${pl.live ? "" : "opacity-50 grayscale hover:opacity-80"}`}>
      <span className="flex flex-wrap items-center gap-1.5">
        <b className="text-sm text-white">{pl.name}</b>
        {pl.live ? <Badge tone="lime">Actif</Badge> : <Badge>Non utilisé</Badge>}
        {pending && <Badge tone="amber">Ajout en attente</Badge>}
        {!pl.active && <Badge tone="red">Désactivé dans l'appli</Badge>}
        {pl.taken_by?.length > 0 && <Badge>Aussi : {[...new Set(pl.taken_by.map((t) => t.partner))].length} autre(s) partenaire(s)</Badge>}
      </span>
      <span className="text-[11px] text-slate-400">{pl.page} · {pl.position}</span>
      <span className="text-[10px] text-slate-500">{pl.component} · {pl.size} · {pl.ratio}</span>
      <span className="pointer-events-none block max-h-56 overflow-hidden rounded-xl">
        <span className="block origin-top scale-[0.62]" style={{ width: "161%", marginLeft: "-30.5%" }}><MockPage placement={pl} ad={ad} highlight={false} /></span>
      </span>
    </button>
  );
}

function CreativeEditor({ cr, partner, load, pl }) {
  const [edit, setEdit] = useState(false);
  const save = async (v) => {
    try { await api.put(`/superadmin/regie/creatives/${cr.id}`, { ...Object.fromEntries(KEEP.map((k) => [k, cr[k] ?? null])), ...v }); toast.success("Visuel enregistré : valide-le pour le remettre en ligne"); setEdit(false); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.post(`/superadmin/regie/creatives/${cr.id}/image`, fd); toast.success("Image importée : à valider"); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const approve = async () => {
    try { await api.post(`/superadmin/regie/creatives/${cr.id}/approve`, {}); toast.success("Visuel validé : il est de nouveau diffusé"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2 rounded-xl border border-white/10 bg-white/[0.02] p-3" data-testid={`ws-creative-${cr.id}`}>
      <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-white">{cr.title}
        <Badge tone={cr.status === "approved" ? "lime" : cr.status === "rejected" ? "red" : "amber"}>{STATUS_FR[cr.status] || "À valider"}</Badge></p>
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setEdit(!edit)} className="mpl-btn-ghost px-2 py-1 text-xs" data-testid={`ws-edit-text-${cr.id}`}>Modifier les textes</button>
        <label className="mpl-btn-ghost cursor-pointer px-2 py-1 text-xs" data-testid={`ws-upload-${cr.id}`}><ImagePlus size={12} /> {cr.image_version ? "Changer l'image" : "Ajouter une image"}<input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} /></label>
        <CreativeFit key={`f-${cr.id}-${cr.image_version}-${JSON.stringify(cr.image_fit || {})}`} c={cr} load={load} />
        <CreativeLayout key={`l-${cr.id}-${pl.code}-${cr.image_version}-${JSON.stringify(cr.placement_layouts?.[pl.code] || cr.layout || {})}-${JSON.stringify(cr.placement_fits?.[pl.code] || {})}`} c={cr} partner={partner} load={load} placement={pl} />
        <CreativeLayout key={`g-${cr.id}-${cr.image_version}-${JSON.stringify(cr.layout || {})}-${JSON.stringify(cr.image_fit || {})}`} c={cr} partner={partner} load={load} />
      </div>
      {cr.status !== "approved" && (
        <p className="flex flex-wrap items-center gap-2 rounded-lg bg-amber-300/10 px-3 py-2 text-xs text-amber-100" data-testid={`ws-needs-approval-${cr.id}`}>
          Ce visuel n'est pas diffusé tant qu'il n'est pas validé.
          <button onClick={approve} className="mpl-btn-primary px-2.5 py-1 text-xs" data-testid={`ws-approve-${cr.id}`}>Valider le visuel</button>
        </p>
      )}
      {edit && <p className="text-[11px] text-amber-300">Après modification, le visuel repasse « à valider » et n'est plus diffusé jusqu'à ta validation.</p>}
      {edit && <EntityForm fields={TEXT_FIELDS} initial={cr} onSubmit={save} onCancel={() => setEdit(false)} testId={`ws-form-${cr.id}`} />}
    </div>
  );
}

function AddToCampaign({ pl, campaigns, load }) {
  const free = campaigns.filter((c) => !c.placements.includes(pl.code) && !c.pending_placements.includes(pl.code));
  const [cid, setCid] = useState(free[0]?.id || "");
  if (!campaigns.length) return <p className="text-xs text-amber-300" data-testid="ws-no-campaign">Ce partenaire n'a encore aucune campagne : crée-la dans l'onglet Campagnes.</p>;
  if (!free.length) return null;
  const add = async () => {
    try {
      const { data } = await api.post(`/superadmin/regie/campaigns/${cid}/placements/${pl.code}`);
      toast.success(data.state === "pending" ? "Ajout en attente : publie-le quand tu es prêt" : "Emplacement ajouté à la campagne (elle reste à valider)");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="ws-add">
      <select className="mpl-input max-w-xs text-xs" value={cid} onChange={(e) => setCid(e.target.value)} data-testid="ws-add-campaign">
        {free.map((c) => <option key={c.id} value={c.id}>{c.name} · {STATUS_FR[c.effective_status] || c.effective_status}</option>)}
      </select>
      <button onClick={add} className="mpl-btn-primary px-3 py-1.5 text-xs" data-testid="ws-add-btn"><Plus size={12} /> Ajouter cet encart</button>
    </div>
  );
}

function PendingRow({ c, code, load }) {
  const act = async (method, suffix, msg) => {
    try { await api[method](`/superadmin/regie/campaigns/${c.id}/placements/${code}${suffix}`); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg bg-amber-300/10 px-3 py-2 text-xs text-amber-100" data-testid={`ws-pending-${c.id}`}>
      En attente dans « {c.name} »
      <button onClick={() => act("post", "/publish", "Encart publié")} className="mpl-btn-primary px-2.5 py-1 text-xs" data-testid={`ws-publish-${c.id}`}><Send size={12} /> Publier sur cet encart</button>
      <button onClick={() => act("delete", "", "Ajout annulé")} className="mpl-btn-ghost px-2.5 py-1 text-xs" data-testid={`ws-drop-${c.id}`}><X size={12} /> Annuler l'ajout</button>
    </div>
  );
}

function TakenWarning({ pl }) {
  if (!pl.taken_by?.length) return null;
  return (
    <p className="rounded-lg bg-white/5 px-3 py-2 text-xs text-slate-300" data-testid={`ws-taken-${pl.code}`}>
      Aussi diffusé sur cet encart (en rotation, selon le ciblage de chaque campagne) : {pl.taken_by.map((t) => `${t.partner} « ${t.campaign} » du ${t.start} au ${t.end}`).join(" · ")}.
    </p>
  );
}

function Rotation({ pl, ads }) {
  const [i, setI] = useState(0);
  const ad = ads[i % ads.length];
  return (
    <div className="space-y-2" data-testid={`ws-rotation-${pl.code}`}>
      {ads.length > 1 && (
        <div className="flex items-center gap-2 text-xs text-slate-300">
          <button onClick={() => setI((i + ads.length - 1) % ads.length)} className="mpl-btn-ghost px-2 py-1" aria-label="Visuel précédent" data-testid="ws-rotation-prev"><ChevronLeft size={14} /></button>
          <span data-testid="ws-rotation-count">Visuel {(i % ads.length) + 1} / {ads.length} en rotation : {ad.title}</span>
          <button onClick={() => setI((i + 1) % ads.length)} className="mpl-btn-ghost px-2 py-1" aria-label="Visuel suivant" data-testid="ws-rotation-next"><ChevronRight size={14} /></button>
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[360px_minmax(0,1fr)]">
        <MockPage placement={pl} ad={ad} />
        {pl.code !== "email_recap" ? <MockPage placement={pl} ad={ad} device="desktop" /> : <p className="text-xs text-slate-500">Emplacement courriel : pas de version ordinateur dans l'appli.</p>}
      </div>
    </div>
  );
}

function PlacementDetail({ pl, data, load }) {
  const { partner, campaigns, creatives } = data;
  const used = campaigns.filter((c) => c.placements.includes(pl.code));
  const ids = [...new Set(used.flatMap((c) => c.creative_ids))];
  const shown = ids.length ? creatives.filter((cr) => ids.includes(cr.id)) : creatives;
  const live = used.filter((c) => pl.live_campaign_ids.includes(c.id)).flatMap((c) => c.creative_ids);
  const rot = creatives.filter((cr) => live.includes(cr.id) && cr.status === "approved");
  const ads = (rot.length ? rot : shown).map((cr) => adFrom(cr, partner));
  if (!ads.length) ads.push(adFrom(null, partner));
  return (
    <section className="mpl-card min-w-0 space-y-4 p-4" data-testid={`ws-detail-${pl.code}`}>
      <div className="space-y-1">
        <p className="mpl-eyebrow">{pl.page}</p>
        <h3 className="font-display text-xl font-bold text-white">{pl.name}</h3>
        <p className="text-xs text-slate-400">{pl.position} · {pl.component} · {pl.size} ({pl.ratio}) · visibilité {pl.visibility}</p>
        <p className="text-[11px] text-slate-500">{pl.restrictions}</p>
      </div>
      {used.length > 0 && <p className="text-xs text-slate-300">Utilisé par : {used.map((c) => `${c.name} (${STATUS_FR[c.effective_status] || c.effective_status})`).join(", ")}</p>}
      {campaigns.filter((c) => c.pending_placements.includes(pl.code)).map((c) => <PendingRow key={c.id} c={c} code={pl.code} load={load} />)}
      <TakenWarning pl={pl} />
      <AddToCampaign key={pl.code} pl={pl} campaigns={campaigns} load={load} />
      <Rotation key={`rot-${pl.code}-${ads.map((a) => a.id).join()}`} pl={pl} ads={ads} />
      <div className="space-y-2">
        <p className="mpl-eyebrow">{ids.length ? "Visuels de cet encart" : "Visuels du partenaire"}</p>
        {shown.length ? shown.map((cr) => <CreativeEditor key={cr.id} cr={cr} partner={partner} load={load} pl={pl} />)
          : <p className="text-xs text-slate-500" data-testid="ws-no-creative">Aucun visuel pour ce partenaire : crée-le dans l'onglet Créations.</p>}
      </div>
    </section>
  );
}

function Workspace({ pid, onExpired }) {
  const [data, load] = useApi(`/superadmin/regie/partners/${pid}/workspace`, onExpired);
  const [code, setCode] = useState(null);
  if (!data) return <Spinner />;
  const { partner, placements, campaigns, creatives } = data;
  const sel = placements.find((p) => p.code === code);
  const adFor = (pl) => {
    const ids = campaigns.filter((c) => c.placements.includes(pl.code)).flatMap((c) => c.creative_ids);
    return adFrom(creatives.find((cr) => ids.includes(cr.id)) || creatives[0], partner);
  };
  const live = placements.filter((p) => p.live).length;
  return (
    <div className="space-y-4" data-testid="ws-partner">
      <div className="flex items-center gap-3">
        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white p-1">{partner.logo_url ? <img src={`${ORIGIN}${partner.logo_url}`} alt="" className="max-h-full max-w-full object-contain" /> : null}</span>
        <div><p className="font-semibold text-white" data-testid="ws-partner-name">{partner.name}</p>
          <p className="text-xs text-slate-400" data-testid="ws-summary">{live} encart{live > 1 ? "s" : ""} actif{live > 1 ? "s" : ""} sur {placements.length} · {campaigns.length} campagne(s) · {creatives.length} visuel(s)</p></div>
      </div>
      <p className="text-[11px] text-slate-500">En couleur : encarts utilisés par une campagne validée en cours. Grisés : encarts non utilisés. Touche un encart pour voir son aperçu et modifier ses visuels.</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {[...placements].sort((a, b) => b.live - a.live).map((pl) => <PlacementTile key={pl.code} pl={pl} ad={adFor(pl)} selected={code === pl.code} onPick={() => setCode(code === pl.code ? null : pl.code)} />)}
      </div>
      {sel && <PlacementDetail pl={sel} data={data} load={load} />}
    </div>
  );
}

export default function PartnerWorkspace({ onExpired, workspacePartner }) {
  const ps = usePartners(onExpired);
  const [pid, setPid] = useState(workspacePartner || "");
  return (
    <div className="space-y-4" data-testid="partner-workspace">
      <label className="block max-w-md text-xs text-slate-300">Partenaire
        <select className="mpl-input mt-1" value={pid} onChange={(e) => setPid(e.target.value)} data-testid="ws-partner-select">
          <option value="">Choisis un partenaire…</option>
          {ps.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </label>
      {pid ? <Workspace key={pid} pid={pid} onExpired={onExpired} /> : <p className="text-sm text-slate-400" data-testid="ws-empty">Choisis un partenaire pour voir tous ses encarts publicitaires.</p>}
    </div>
  );
}
